import ReactDOM from "react-dom";

const MOCKUP_SELECTOR = "main[data-staging-mockup]";

let registeredContainer = null;

/**
 * Look up the staging-mockup <main> element.
 *
 * Present on the staging Cloud Run instance (where index.html is served),
 * absent in production (where the React tree is mounted inside the Lexis+
 * host page). The other functions in this module use a null result here
 * as their no-op signal.
 *
 * @returns {HTMLElement|null}
 */
function getMockupMain() {
  return document.querySelector(MOCKUP_SELECTOR);
}

/**
 * Remember the DOM container that React was rendered into so a later
 * unmountApp() call can hand it to ReactDOM.unmountComponentAtNode.
 *
 * Safe to call unconditionally from the app bootstrap. In production the
 * stored reference is simply never read because unmountApp() short-circuits
 * when the staging mockup is absent.
 *
 * @param {HTMLElement} container - The element passed to ReactDOM.render.
 * @returns {void}
 */
export function registerRoot(container) {
  registeredContainer = container;
}

/**
 * Reveal the staging mockup by adding the "active" class to <main>.
 *
 * Wired into the Axios response interceptor so the first successful API
 * call unhides the UI. No-op in production (no mockup element) and
 * idempotent on staging — repeated calls leave a single "active" class.
 *
 * @returns {void}
 */
export function showMain() {
  const main = getMockupMain();
  if (!main) return;
  main.classList.add("active");
}

/**
 * Tear the React app down and remove the staging mockup from the DOM.
 *
 * Wired into the Axios response interceptor so a 401/403 from the API
 * removes any trace of the UI for unauthenticated visitors on the public
 * staging URL. No-op in production — the production host page is never
 * mutated and the React tree is never unmounted from beneath the host.
 *
 * @returns {void}
 */
export function unmountApp() {
  const main = getMockupMain();
  if (!main) return;
  if (registeredContainer) {
    ReactDOM.unmountComponentAtNode(registeredContainer);
  }
  main.remove();
}
